/*
 * wtb-cart-guard.js — QEP "Where to Buy" front-end enforcement
 * -----------------------------------------------------------------------------
 * Two layers of defense that run entirely in the theme, without touching Swym's
 * UI:
 *
 *   Layer 2 — Add-to-cart interceptor:
 *     Wraps window.fetch and XMLHttpRequest. Any POST to /cart/add(.js) for a
 *     browse-only product is blocked (if the variant is already known) or
 *     reconciled away immediately after it lands.
 *
 *   Layer 3 — Cart reconciliation guard:
 *     On page load and on cart changes, sweeps /cart.js, removes any browse-only
 *     line, and disables checkout controls while one is present.
 *
 * A product is "browse-only" when it carries the Swym tag `swym-hide-addtocart`
 * — the same signal Swym uses to hide the button. We read it client-side from
 * /products/{handle}.js (which exposes `tags`).
 *
 * KNOWN LIMITS (require server-side enforcement to close — Plus or a public-app
 * validation function):
 *   • A raw POST /cart/add.js made outside this page (curl, or a devtools fetch
 *     that bypasses this wrapper) is not caught pre-add; it is only reconciled
 *     if it happens in this page context.
 *   • The /cart/{variant}:1 checkout permalink adds and 302-redirects straight
 *     to /checkout, giving this script no chance to run.
 *
 * Install: see README. Load once, sitewide, before </body>.
 */
(function () {
  "use strict";

  /* ------------------------------- Config -------------------------------- */
  var WTB_TAG = "swym-hide-addtocart"; // lowercase; matched case-insensitively
  // Fallback if the theme setting is empty. The live message comes from the
  // "Where to Buy Guard" theme setting (window.WTB_CONFIG.message), which is
  // client-editable and translatable via Translate & Adapt. Use [product] to
  // insert the product name.
  var DEFAULT_MESSAGE =
    "[product] isn’t available for online purchase. Use the “Where to Buy” option on the product page to find a retailer.";

  /**
   * Build the shown message: pull the template from the theme setting (or the
   * fallback), then substitute the [product] token with the product name — or a
   * generic noun when the name isn't known (e.g. a pre-blocked add).
   * @param {string} [title]
   */
  function buildMessage(title) {
    var tmpl =
      (window.WTB_CONFIG && typeof window.WTB_CONFIG.message === "string" && window.WTB_CONFIG.message.trim()) ||
      DEFAULT_MESSAGE;
    var name = title ? "“" + title + "”" : "This item";
    return tmpl.replace(/\[product\]/gi, name);
  }
  // Selectors for checkout buttons to disable while a browse-only item lingers.
  var CHECKOUT_SELECTORS = [
    '[name="checkout"]',
    '[name="goto_pp"]',
    'a[href="/checkout"]',
    'a[href^="/checkout"]',
    ".cart__checkout",
    "#checkout",
    "[data-checkout-button]",
  ].join(",");

  /* ------------------------------ Internals ------------------------------ */
  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;
  var productCache = new Map(); // handle -> boolean (isBrowseOnly)
  var inFlight = new Map(); // handle -> Promise<boolean>
  var wtbVariantIds = new Set(); // variant ids known to be browse-only (strings)
  var reconciling = false;

  function log() {
    if (window.WTB_DEBUG && window.console)
      console.log.apply(console, ["[wtb-guard]"].concat([].slice.call(arguments)));
  }

  function isAddUrl(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(String(url || ""));
  }

  /* Extract variant id(s) from any /cart/add request body shape. */
  function extractVariantIds(body) {
    var ids = [];
    try {
      if (!body) return ids;
      if (typeof body === "string") {
        try {
          collectIds(JSON.parse(body), ids);
          return ids;
        } catch (e) {
          /* not JSON — fall through to form-encoded */
        }
        var params = new URLSearchParams(body);
        if (params.get("id")) ids.push(String(params.get("id")));
        return ids;
      }
      if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
        if (body.get("id")) ids.push(String(body.get("id")));
        return ids;
      }
      if (typeof FormData !== "undefined" && body instanceof FormData) {
        var v = body.get("id");
        if (v) ids.push(String(v));
        return ids;
      }
      if (typeof body === "object") collectIds(body, ids);
    } catch (e) {
      /* ignore */
    }
    return ids;
  }

  function collectIds(obj, ids) {
    if (!obj) return;
    if (Array.isArray(obj.items)) {
      obj.items.forEach(function (it) {
        if (it && it.id != null) ids.push(String(it.id));
      });
    } else if (obj.id != null) {
      ids.push(String(obj.id));
    }
  }

  /* Handles of products in a /cart/add.js response (single or items[]). */
  function handlesFromAddResponse(data) {
    var out = [];
    if (!data) return out;
    if (Array.isArray(data.items)) {
      data.items.forEach(function (it) {
        if (it && it.handle) out.push(it.handle);
      });
    } else if (data.handle) {
      out.push(data.handle);
    }
    return out;
  }

  /* Is this product handle browse-only? Cached; also learns its variant ids. */
  function isBrowseOnly(handle) {
    if (!handle) return Promise.resolve(false);
    if (productCache.has(handle)) return Promise.resolve(productCache.get(handle));
    if (inFlight.has(handle)) return inFlight.get(handle);

    var p = (nativeFetch || window.fetch)("/products/" + handle + ".js", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then(function (r) {
        return r && r.ok ? r.json() : null;
      })
      .then(function (product) {
        var flag =
          !!product &&
          Array.isArray(product.tags) &&
          product.tags.some(function (t) {
            return String(t).trim().toLowerCase() === WTB_TAG;
          });
        productCache.set(handle, flag);
        if (flag && product && Array.isArray(product.variants)) {
          product.variants.forEach(function (v) {
            if (v && v.id != null) wtbVariantIds.add(String(v.id));
          });
        }
        inFlight.delete(handle);
        return flag;
      })
      .catch(function () {
        inFlight.delete(handle);
        return false;
      });

    inFlight.set(handle, p);
    return p;
  }

  function getCart() {
    return (nativeFetch || window.fetch)("/cart.js", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then(function (r) {
        return r && r.ok ? r.json() : { items: [] };
      })
      .catch(function () {
        return { items: [] };
      });
  }

  function removeVariants(variantIds) {
    var updates = {};
    variantIds.forEach(function (id) {
      updates[id] = 0;
    });
    return (nativeFetch || window.fetch)("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ updates: updates }),
    }).catch(function () {
      /* ignore */
    });
  }

  function setCheckoutDisabled(disabled) {
    try {
      document.querySelectorAll(CHECKOUT_SELECTORS).forEach(function (el) {
        if (disabled) {
          el.setAttribute("disabled", "disabled");
          el.setAttribute("aria-disabled", "true");
          el.style.pointerEvents = "none";
          el.style.opacity = "0.5";
        } else {
          el.removeAttribute("disabled");
          el.removeAttribute("aria-disabled");
          el.style.pointerEvents = "";
          el.style.opacity = "";
        }
      });
    } catch (e) {
      /* ignore */
    }
  }

  /* Fake failed response so callers treat a pre-blocked add as an error. */
  function blockedResponse() {
    var payload = { status: 422, message: "Unavailable", description: buildMessage() };
    if (typeof Response !== "undefined") {
      return new Response(JSON.stringify(payload), {
        status: 422,
        headers: { "Content-Type": "application/json" },
      });
    }
    return Promise.reject(payload);
  }

  /* ------------------------------ Layer 3 -------------------------------- */
  function reconcileCart() {
    if (reconciling) return Promise.resolve();
    reconciling = true;
    return getCart()
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        return Promise.all(
          items.map(function (it) {
            return isBrowseOnly(it.handle).then(function (wtb) {
              return { item: it, wtb: wtb };
            });
          })
        );
      })
      .then(function (results) {
        var bad = results.filter(function (r) {
          return r.wtb;
        });
        if (!bad.length) {
          setCheckoutDisabled(false);
          return;
        }
        setCheckoutDisabled(true);
        var ids = bad.map(function (r) {
          return r.item.variant_id != null ? r.item.variant_id : r.item.id;
        });
        var title = bad[0].item.product_title || bad[0].item.title || "";
        return removeVariants(ids).then(function () {
          notifyBlocked(title);
          setCheckoutDisabled(false);
          refreshCartUI();
          log("removed browse-only lines:", ids);
        });
      })
      .catch(function (e) {
        log("reconcile error", e);
      })
      .then(function () {
        reconciling = false;
      });
  }

  /* --------------------------- Notifications ----------------------------- */
  function notifyBlocked(title) {
    var msg = buildMessage(title);

    // Let the theme handle it if it wants to.
    var ev;
    try {
      ev = new CustomEvent("wtb:blocked", { bubbles: true, cancelable: true, detail: { message: msg, title: title } });
      document.dispatchEvent(ev);
    } catch (e) {
      ev = { defaultPrevented: false };
    }
    if (ev.defaultPrevented) return;

    // Default lightweight toast.
    try {
      ensureToastStyles();
      var toast = document.createElement("div");
      toast.className = "wtb-toast";
      toast.setAttribute("role", "alert");
      toast.textContent = msg;
      document.body.appendChild(toast);
      requestAnimationFrame(function () {
        toast.classList.add("wtb-toast--in");
      });
      setTimeout(function () {
        toast.classList.remove("wtb-toast--in");
        setTimeout(function () {
          toast.remove();
        }, 300);
      }, 5000);
    } catch (e) {
      /* ignore */
    }
  }

  function ensureToastStyles() {
    if (document.getElementById("wtb-toast-styles")) return;
    var s = document.createElement("style");
    s.id = "wtb-toast-styles";
    s.textContent =
      ".wtb-toast{position:fixed;left:50%;bottom:24px;transform:translate(-50%,16px);" +
      "max-width:420px;width:calc(100% - 32px);background:#1a1a1a;color:#fff;padding:14px 18px;" +
      "border-radius:8px;box-shadow:0 8px 30px rgba(0,0,0,.25);font-size:14px;line-height:1.4;" +
      "z-index:2147483647;opacity:0;transition:opacity .3s ease,transform .3s ease;}" +
      ".wtb-toast--in{opacity:1;transform:translate(-50%,0);}";
    document.head.appendChild(s);
  }

  /*
   * refreshCartUI — best-effort nudge for the theme to re-render its cart/drawer
   * after we remove a line. Themes vary; wire this to your theme's own refresh
   * if the drawer doesn't update. As a guaranteed fallback we reload the /cart
   * page (only when the customer is actually on it).
   */
  function refreshCartUI() {
    // This theme (Maestrooo/Impact family) re-renders its cart/drawer when
    // "cart:refresh" is dispatched on document. Confirmed via theme.js.
    try {
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    } catch (e) {
      /* ignore */
    }
    // Extra generic events for portability across themes.
    ["cart:updated", "cart:change"].forEach(function (name) {
      try {
        document.documentElement.dispatchEvent(new CustomEvent(name, { bubbles: true }));
      } catch (e) {
        /* ignore */
      }
    });
    if (/\/cart(\/|$|\?)/.test(window.location.pathname + window.location.search)) {
      // On the cart page a hard refresh is the reliable way to reflect removal.
      setTimeout(function () {
        window.location.reload();
      }, 900);
    }
  }

  /* ------------------------------ Layer 2 -------------------------------- */
  // fetch
  if (nativeFetch) {
    window.fetch = function (input, init) {
      try {
        var url = typeof input === "string" ? input : (input && input.url) || "";
        var method = ((init && init.method) || (input && input.method) || "GET").toUpperCase();
        if (isAddUrl(url) && method === "POST") {
          var body = (init && init.body) || (input && input.body);
          var ids = extractVariantIds(body);
          var known = ids.filter(function (id) {
            return wtbVariantIds.has(String(id));
          });
          if (known.length) {
            log("pre-blocked add for known browse-only variant(s):", known);
            notifyBlocked();
            return Promise.resolve(blockedResponse());
          }
          // Unknown variant: allow, then inspect the response and reconcile.
          return nativeFetch(input, init).then(function (res) {
            try {
              res
                .clone()
                .json()
                .then(function (data) {
                  var handles = handlesFromAddResponse(data);
                  return Promise.all(handles.map(isBrowseOnly)).then(function (flags) {
                    if (
                      flags.some(function (f) {
                        return f;
                      })
                    ) {
                      notifyBlocked(data && (data.product_title || data.title));
                      reconcileCart();
                    }
                  });
                })
                .catch(function () {});
            } catch (e) {
              /* ignore */
            }
            return res;
          });
        }
      } catch (e) {
        /* ignore and fall through */
      }
      return nativeFetch(input, init);
    };
  }

  // XMLHttpRequest (send-then-reconcile; covers older Ajax carts)
  try {
    var XHR = window.XMLHttpRequest;
    if (XHR && XHR.prototype) {
      var open = XHR.prototype.open;
      var send = XHR.prototype.send;
      XHR.prototype.open = function (method, url) {
        this.__wtb = { method: (method || "GET").toUpperCase(), url: url || "" };
        return open.apply(this, arguments);
      };
      XHR.prototype.send = function (body) {
        var meta = this.__wtb;
        if (meta && meta.method === "POST" && isAddUrl(meta.url)) {
          var ids = extractVariantIds(body);
          var known = ids.filter(function (id) {
            return wtbVariantIds.has(String(id));
          });
          if (known.length) {
            log("XHR pre-block for known browse-only variant(s):", known);
            notifyBlocked();
            // Reconcile shortly after in case the add still went through.
            setTimeout(reconcileCart, 0);
          }
          this.addEventListener("load", function () {
            try {
              var data = JSON.parse(this.responseText || "null");
              handlesFromAddResponse(data).forEach(function (h) {
                isBrowseOnly(h).then(function (wtb) {
                  if (wtb) {
                    notifyBlocked(data && (data.product_title || data.title));
                    reconcileCart();
                  }
                });
              });
            } catch (e) {
              /* ignore */
            }
          });
        }
        return send.apply(this, arguments);
      };
    }
  } catch (e) {
    /* ignore */
  }

  /* ------------------------------ Bootstrap ------------------------------ */
  function warmCurrentProduct() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (m && m[1]) isBrowseOnly(decodeURIComponent(m[1]));
  }

  function boot() {
    warmCurrentProduct();
    reconcileCart();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  // Re-check when returning to a cached page (back/forward) and on cart events.
  window.addEventListener("pageshow", reconcileCart);
  ["cart:updated", "cart:refresh", "cart:change"].forEach(function (name) {
    document.addEventListener(name, function () {
      // Debounced light re-sweep; won't loop because reconcile no-ops when clean.
      setTimeout(reconcileCart, 250);
    });
  });

  // Public handle for manual use/debugging.
  window.WTBGuard = {
    reconcile: reconcileCart,
    isBrowseOnly: isBrowseOnly,
    knownVariants: wtbVariantIds,
    config: { tag: WTB_TAG },
  };
})();
